import { Injectable, signal } from '@angular/core';
import { GridApi, IRowNode } from 'ag-grid-community';

@Injectable()
export class GridManagerService {
  readonly gridApi = signal<GridApi | undefined>(undefined);
  private readonly registeredGridApis = new Set<GridApi>();

  setGridApi(gridApi: GridApi): void {
    this.gridApi.set(gridApi);
  }

  registerGridApi(gridApi: GridApi): void {
    this.registeredGridApis.add(gridApi);
  }

  unregisterGridApi(gridApi: GridApi): void {
    this.registeredGridApis.delete(gridApi);
  }

  deselectAllExcept(excludeApi: GridApi): void {
    this.registeredGridApis.forEach((api) => {
      if (api !== excludeApi && !api.isDestroyed()) {
        api.deselectAll();
      }
    });
  }

  deselectAll(): void {
    this.registeredGridApis.forEach((api) => {
      if (!api.isDestroyed()) {
        api.deselectAll();
      }
    });
  }

  exportDataAsCsv(fileName: string): boolean {
    const gridApi = this.gridApi();
    if (!gridApi || gridApi.isDestroyed()) return false;

    try {
      gridApi.exportDataAsCsv({ fileName });
      return true;
    } catch (error) {
      console.error('Failed to export grid data as CSV.', error);
      return false;
    }
  }

  startLoading(): void {
    const gridApi = this.gridApi();
    if (!gridApi || gridApi.isDestroyed()) return;

    gridApi.setGridOption('loading', true);
    this.clearGrid(gridApi);
  }

  stopLoading(): void {
    const gridApi = this.gridApi();
    if (!gridApi || gridApi.isDestroyed()) return;

    gridApi.setGridOption('loading', false);
  }

  selectRows(shouldSelectNode: (node: IRowNode<unknown>) => boolean): void {
    const gridApi = this.gridApi();
    if (!gridApi || gridApi.isDestroyed()) {
      console.warn('Grid API is not available or has been destroyed.');
      return;
    }

    gridApi.forEachNode((node) => {
      if (shouldSelectNode(node)) {
        node.setSelected(true);
      }
    });
  }

  private clearGrid(gridApi: GridApi): void {
    const rowData: unknown[] = [];

    gridApi.forEachNode((node) => {
      if (node.data) {
        rowData.push(node.data);
      }
    });

    gridApi.applyTransaction({
      remove: rowData,
    });
  }
}
